/**
 * TurboVets postMessage autofill.
 *
 * Accepts ONLY TURBOVETS_PREFILL messages from exact allowlisted origins.
 * Untrusted payloads are parsed with runtime type guards, unknown fields are
 * ignored, and the form is NEVER auto-submitted; the user must review and
 * submit. See docs/autofill-contract.md.
 */
import { applyPrefill } from "./form/referral-form";

export const PREFILL_MESSAGE_TYPE = "TURBOVETS_PREFILL";

export interface PrefillMessage {
  type: typeof PREFILL_MESSAGE_TYPE;
  correlationId?: string;
  payload: Record<string, unknown>;
}

export interface PrefillOutcome {
  applied: boolean;
  reason?: string;
  fields: string[];
  correlationId?: string;
}

export interface PrefillHandlerOptions {
  allowedOrigins: readonly string[];
  root: ParentNode;
  /**
   * Applies the mapped values of an accepted payload and returns the field keys
   * that were actually applied. Defaults to applying them to the legacy form
   * controls under `root`. The refinance wizard entry path supplies its own
   * applier because it mounts no legacy form to apply values to.
   */
  apply?: (mapped: Readonly<Record<string, string>>) => string[];
  onApplied?: (fields: string[], correlationId?: string) => void;
  /**
   * Invoked once per inbound message from an allowed origin (applied, ignored,
   * or rejected), with the resolved outcome and the source event. Never invoked
   * for disallowed origins. Used to emit the outbound `LD_TURBOVETS_PREFILL_ACK`
   * acknowledgement without coupling this module to the messaging layer.
   */
  onAck?: (outcome: PrefillOutcome, event: MessageEvent) => void;
  /**
   * Invoked immediately before an accepted payload is applied to the form, so
   * callers can tell apart a control change caused by prefill from one made by
   * the user and defer any re-render until the whole payload has been applied.
   */
  onBeforeApply?: () => void;
  /**
   * Invoked with the mapped values of an accepted payload from an allowed
   * origin, before they are applied. Some Wave 5 refinance answers (the
   * application-level military answer) have no legacy form control, so this is
   * how they reach the refinance wizard. Never invoked for a disallowed origin
   * or a malformed message.
   */
  onMapped?: (mapped: Readonly<Record<string, string>>) => void;
}

/**
 * Maps a simple (flat) payload key to a form control `name`. Simple keys are
 * treated as borrower fields plus the top-level property/loan fields.
 */
const SIMPLE_KEY_MAP: Readonly<Record<string, string>> = {
  firstName: "borrower.firstName",
  lastName: "borrower.lastName",
  email: "borrower.email",
  phone: "borrower.phone",
  creditScoreRange: "borrower.creditScoreRange",
  streetAddress: "borrower.streetAddress",
  city: "borrower.city",
  state: "borrower.state",
  zipCode: "borrower.zipCode",
  partnerReferenceId: "partnerReferenceId",
  loanPurpose: "loanPurpose",
  propertyStreetAddress: "propertyStreetAddress",
  propertyCity: "propertyCity",
  propertyState: "propertyState",
  propertyZipCode: "propertyZipCode",
  propertyType: "propertyType",
  estimatedValue: "estimatedValue",
  loanAmount: "loanAmount",
  currentLoanBalance: "currentLoanBalance",
  refinanceType: "refinanceType",
  // The application-level military answer and component are wizard-only values
  // and pass through under their own names; the legacy form has no control for
  // them, so applying them is a no-op there.
  militaryAnswer: "militaryAnswer",
  militaryComponent: "militaryComponent",
};

const BORROWER_SUBFIELDS: Readonly<Record<string, string>> = {
  firstName: "firstName",
  lastName: "lastName",
  email: "email",
  phone: "phone",
  creditScoreRange: "creditScoreRange",
  streetAddress: "streetAddress",
  city: "city",
  state: "state",
  zipCode: "zipCode",
};

function scalarToString(value: unknown): string | undefined {
  if (typeof value === "string") return value;
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  if (typeof value === "boolean") return value ? "true" : "false";
  return undefined;
}

/**
 * Flattens one nested borrower object into its scoped control names. Mapping is
 * transport only: it says what a payload carries, never what a surface accepts.
 * The legacy form has a control for each mapped name, while the step journeys
 * take the co-borrower subset their own model defines (WI-TV-W7-001) — names,
 * phone, email, and the optional mailing address, with `creditScoreRange`
 * ignored, because there is no co-borrower credit answer to hold it.
 */
function mapBorrower(
  raw: unknown,
  prefix: "borrower" | "coBorrower",
  target: Record<string, string>,
): void {
  if (typeof raw !== "object" || raw === null) return;
  const obj = raw as Record<string, unknown>;
  for (const [key, name] of Object.entries(BORROWER_SUBFIELDS)) {
    const str = scalarToString(obj[key]);
    if (str !== undefined) target[`${prefix}.${name}`] = str;
  }
}

/**
 * Translates an untrusted prefill payload into a flat map of form control
 * `name` -> string value. Supports both simple keys (firstName, email, ...) and
 * nested API-style objects (borrower, coBorrower, militaryService, employment).
 * Unknown keys are ignored.
 */
export function mapPrefillPayload(payload: Record<string, unknown>): Record<string, string> {
  const target: Record<string, string> = {};

  for (const [key, value] of Object.entries(payload)) {
    const mappedName = SIMPLE_KEY_MAP[key];
    if (mappedName) {
      const str = scalarToString(value);
      if (str !== undefined) target[mappedName] = str;
    }
  }

  mapBorrower(payload.borrower, "borrower", target);
  mapBorrower(payload.coBorrower, "coBorrower", target);

  const military = payload.militaryService;
  if (typeof military === "object" && military !== null) {
    const m = military as Record<string, unknown>;
    const isMilitary = scalarToString(m.isMilitary);
    const branch = scalarToString(m.branch);
    const serviceStatus = scalarToString(m.serviceStatus);
    if (isMilitary !== undefined) target["isMilitary"] = isMilitary;
    if (branch !== undefined) target["militaryBranch"] = branch;
    if (serviceStatus !== undefined) target["militaryServiceStatus"] = serviceStatus;
  }

  const employment = payload.employment;
  if (typeof employment === "object" && employment !== null) {
    const e = employment as Record<string, unknown>;
    const employmentStatus = scalarToString(e.employmentStatus);
    const incomeRange = scalarToString(e.incomeRange);
    if (employmentStatus !== undefined) target["employmentStatus"] = employmentStatus;
    if (incomeRange !== undefined) target["incomeRange"] = incomeRange;
  }

  return target;
}

/** Runtime type guard for the TURBOVETS_PREFILL message envelope. */
export function isPrefillMessage(data: unknown): data is PrefillMessage {
  if (typeof data !== "object" || data === null) return false;
  const obj = data as Record<string, unknown>;
  if (obj.type !== PREFILL_MESSAGE_TYPE) return false;
  if (typeof obj.payload !== "object" || obj.payload === null) return false;
  if (obj.correlationId !== undefined && typeof obj.correlationId !== "string") return false;
  return true;
}

function isOriginAllowed(origin: string, allowedOrigins: readonly string[]): boolean {
  return allowedOrigins.includes("*") || allowedOrigins.includes(origin);
}

/**
 * Validates and applies a single postMessage event. Never submits the form.
 */
export function handlePrefillMessage(
  event: MessageEvent,
  options: PrefillHandlerOptions,
): PrefillOutcome {
  if (!isOriginAllowed(event.origin, options.allowedOrigins)) {
    return { applied: false, reason: "disallowed-origin", fields: [] };
  }

  if (!isPrefillMessage(event.data)) {
    return { applied: false, reason: "invalid-shape", fields: [] };
  }

  const mapped = mapPrefillPayload(event.data.payload);
  if (Object.keys(mapped).length === 0) {
    return {
      applied: false,
      reason: "no-known-fields",
      fields: [],
      ...(event.data.correlationId !== undefined
        ? { correlationId: event.data.correlationId }
        : {}),
    };
  }

  if (options.onMapped) options.onMapped(mapped);
  if (options.onBeforeApply) options.onBeforeApply();
  const applied = options.apply ? options.apply(mapped) : applyPrefill(options.root, mapped);
  if (options.onApplied) options.onApplied(applied, event.data.correlationId);

  return {
    applied: applied.length > 0,
    fields: applied,
    ...(event.data.correlationId !== undefined ? { correlationId: event.data.correlationId } : {}),
  };
}

/**
 * Installs the window message listener. Returns a disposer that removes it.
 */
export function installAutofillListener(options: PrefillHandlerOptions): () => void {
  const listener = (event: MessageEvent): void => {
    const outcome = handlePrefillMessage(event, options);
    // Acknowledge every message from an allowed origin; stay silent for
    // disallowed origins (the only case that yields this reason).
    if (options.onAck && outcome.reason !== "disallowed-origin") {
      options.onAck(outcome, event);
    }
  };
  window.addEventListener("message", listener);
  return () => window.removeEventListener("message", listener);
}
